import React, { useState, useMemo } from "react";
import yaml from 'js-yaml';
import {
  ExclamationTriangleIcon,
  DocumentTextIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";

// Types
type MetadataValue = string | number | boolean | null | undefined | MetadataValue[] | { [key: string]: MetadataValue };

interface DraftMetadataPreviewProps {
  contentText: string;
}

interface MetadataFieldItemProps {
  fieldKey: string;
  value: MetadataValue;
  isExpanded: boolean;
  onToggle: () => void;
}

interface MetadataValueProps {
  value: MetadataValue;
}

/**
 * DraftMetadataPreview Component
 * Renders draft_metadata.yaml as generic key-value pairs
 * Handles any metadata structure dynamically
 */
export default function DraftMetadataPreview_Component({ contentText }: DraftMetadataPreviewProps): JSX.Element {
  const [expandedFields, setExpandedFields] = useState<Set<string>>(new Set());

  // Parse YAML and handle errors
  const { parsedData, parseError } = useMemo(() => {
    try {
      const data = yaml.load(contentText) as Record<string, MetadataValue>;
      return { parsedData: data, parseError: null };
    } catch (error) {
      return { parsedData: null, parseError: (error as Error).message };
    }
  }, [contentText]);

  const toggleField = (key: string): void => {
    const newExpanded = new Set(expandedFields);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedFields(newExpanded);
  };

  const toggleAll = (expand: boolean): void => {
    if (expand && parsedData) {
      setExpandedFields(new Set(Object.keys(parsedData)));
    } else {
      setExpandedFields(new Set());
    }
  };

  // Error state - YAML parsing failed
  if (parseError) {
    return (
      <div className="h-full overflow-auto p-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-start space-x-3">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-lg font-semibold text-red-900 mb-2">
                  Invalid YAML Format
                </h3>
                <p className="text-sm text-red-700 mb-3">
                  The metadata YAML file could not be parsed. Please check the file format.
                </p>
                <pre className="text-xs text-red-600 bg-red-100 p-3 rounded font-mono overflow-x-auto">
                  {parseError}
                </pre>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No parsed data
  if (!parsedData || typeof parsedData !== 'object') {
    return (
      <div className="h-full overflow-auto p-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <div className="flex items-start space-x-2">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-yellow-900 mb-1">
                  No Metadata Available
                </h4>
                <p className="text-xs text-yellow-700">
                  The metadata file appears to be empty or has no data.
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <pre className="text-xs text-gray-700 font-mono leading-relaxed whitespace-pre-wrap">
              {contentText}
            </pre>
          </div>
        </div>
      </div>
    );
  }

  const fieldCount = Object.keys(parsedData).length;

  // Render all metadata fields as key-value pairs
  return (
    <div className="h-full overflow-auto bg-gray-50">
      <div className="max-w-5xl mx-auto py-6 px-4">
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <DocumentTextIcon className="h-5 w-5 text-cvc-blue" />
              <h2 className="text-base font-semibold text-gray-900">Draft Metadata</h2>
              <span className="text-xs text-gray-500">
                ({fieldCount} fields)
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => toggleAll(true)}
                className="text-xs text-cvc-blue hover:text-cvc-blue/80 font-medium"
              >
                Expand All
              </button>
              <span className="text-gray-300">|</span>
              <button
                onClick={() => toggleAll(false)}
                className="text-xs text-cvc-blue hover:text-cvc-blue/80 font-medium"
              >
                Collapse All
              </button>
            </div>
          </div>

          <div className="divide-y divide-gray-100">
            {Object.entries(parsedData).map(([key, value]) => (
              <MetadataFieldItem
                key={key}
                fieldKey={key}
                value={value as MetadataValue}
                isExpanded={expandedFields.has(key)}
                onToggle={() => toggleField(key)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Metadata Field Item Component
 * Renders a single key-value pair with collapsible content
 */
function MetadataFieldItem({ fieldKey, value, isExpanded, onToggle }: MetadataFieldItemProps): JSX.Element {
  // Format the key for display (snake_case to Title Case)
  const displayKey = fieldKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

  // Get preview text
  const getPreviewText = (): string => {
    if (typeof value === 'string') {
      return value.length > 80 ? value.substring(0, 80) + '...' : value;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    if (Array.isArray(value)) {
      return `Array (${value.length} items)`;
    }
    if (typeof value === 'object' && value !== null) {
      return `Object (${Object.keys(value).length} fields)`;
    }
    return 'null';
  };

  return (
    <div className="hover:bg-gray-50 transition-colors">
      <div
        onClick={onToggle}
        className="w-full flex items-start justify-between p-4 cursor-pointer"
      >
        <div className="flex items-start space-x-3 flex-1 min-w-0">
          {isExpanded ? (
            <ChevronDownIcon className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
          ) : (
            <ChevronRightIcon className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
          )}
          <div className="flex-1 min-w-0">
            <div className="font-medium text-gray-900 text-sm mb-1">{displayKey}</div>
            {!isExpanded && (
              <div className="text-sm text-gray-600 truncate">
                {getPreviewText()}
              </div>
            )}
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="px-4 pb-4 pl-12">
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <MetadataValueRenderer value={value} />
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Metadata Value Renderer
 * Handles different value types (string, array, object, etc.)
 */
function MetadataValueRenderer({ value }: MetadataValueProps): JSX.Element {
  // String or primitive
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return (
      <div className="text-sm text-gray-900 whitespace-pre-wrap break-words">
        {String(value)}
      </div>
    );
  }

  // Null/undefined
  if (value === null || value === undefined) {
    return <div className="text-sm text-gray-500 italic">null</div>;
  }

  // Array
  if (Array.isArray(value)) {
    return (
      <div className="space-y-2">
        {value.map((item, idx) => (
          <div key={idx} className="border-l-2 border-blue-200 pl-3">
            <div className="text-xs text-gray-500 mb-1">Item {idx + 1}</div>
            <MetadataValueRenderer value={item} />
          </div>
        ))}
      </div>
    );
  }

  // Object
  if (typeof value === 'object') {
    return (
      <div className="space-y-3">
        {Object.entries(value).map(([key, val]) => (
          <div key={key} className="border-l-2 border-gray-200 pl-3">
            <div className="text-xs font-medium text-gray-700 uppercase tracking-wide mb-1">
              {key.replace(/_/g, ' ')}
            </div>
            <MetadataValueRenderer value={val} />
          </div>
        ))}
      </div>
    );
  }

  // Fallback
  return (
    <pre className="text-xs text-gray-700 font-mono overflow-x-auto whitespace-pre-wrap">
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}
